import { useEffect, useRef, useState } from 'react';
import { LocalAudioTrack } from 'twilio-video';
import useParticipants from '../useParticipants/useParticipants';

export default function useVisperHttpTranscription(
  audioTrack: LocalAudioTrack | undefined,
  onText: (text: string) => void,
  participantName: string
) {
  const participants = useParticipants();
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const lastRequestRef = useRef<number>(0);
  const fullTranscriptRef = useRef<string>('');
  let isEndOfSession = false;

  useEffect(() => {
    if (!audioTrack) return;

    const url = process.env.REACT_APP_VISPER_HTTP_URL;
    const apiKey = process.env.REACT_APP_VISPER_API_KEY;

    if (!url) {
      setError('Missing REACT_APP_VISPER_HTTP_URL');
      return;
    }

    const audioContext = new AudioContext({ sampleRate: 16000 });
    const source = audioContext.createMediaStreamSource(new MediaStream([audioTrack.mediaStreamTrack]));
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    source.connect(processor);
    processor.connect(audioContext.destination);

    const chunks: Int16Array[] = [];
    controllerRef.current = new AbortController();
    setIsStreaming(true);

    const floatTo16BitPCM = (float32Array: Float32Array): Int16Array => {
      const buffer = new ArrayBuffer(float32Array.length * 2);
      const view = new DataView(buffer);
      let offset = 0;
      for (let i = 0; i < float32Array.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      }
      return new Int16Array(buffer);
    };

    const createWavBlob = (buffers: Int16Array[], sampleRate: number, numChannels: number): Blob => {
      const totalLength = buffers.reduce((sum, arr) => sum + arr.length, 0);
      const interleaved = new Int16Array(totalLength);
      let offset = 0;
      for (const arr of buffers) {
        interleaved.set(arr, offset);
        offset += arr.length;
      }

      const buffer = new ArrayBuffer(44 + interleaved.length * 2);
      const view = new DataView(buffer);

      const writeString = (offset: number, str: string) => {
        for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
      };

      writeString(0, 'RIFF');
      view.setUint32(4, 36 + interleaved.length * 2, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);
      view.setUint16(22, numChannels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * numChannels * 2, true);
      view.setUint16(32, numChannels * 2, true);
      view.setUint16(34, 16, true);
      writeString(36, 'data');
      view.setUint32(40, interleaved.length * 2, true);

      let index = 44;
      for (let i = 0; i < interleaved.length; i++, index += 2) {
        view.setInt16(index, interleaved[i], true);
      }

      return new Blob([view], { type: 'audio/wav' });
    };

    processor.onaudioprocess = async (e: AudioProcessingEvent) => {
      const input = e.inputBuffer.getChannelData(0);
      const pcm16 = floatTo16BitPCM(input);
      chunks.push(pcm16);

      const now = Date.now();
      if (now - lastRequestRef.current > 5000 && chunks.length > 0) {
        lastRequestRef.current = now;

        const totalLength = chunks.reduce((sum, arr) => sum + arr.length, 0);
        const merged = new Int16Array(totalLength);
        let offset = 0;
        for (const arr of chunks) {
          merged.set(arr, offset);
          offset += arr.length;
        }

        const wavBlob = createWavBlob([merged], 16000, 1);
        chunks.length = 0;

        const formData = new FormData();
        formData.append('file', wavBlob, 'audio.wav');
        formData.append('model', 'whisper-1');
        formData.append('language', 'en');

        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {},
            body: formData,
            signal: controllerRef.current?.signal,
          });

          if (!res.ok) {
            const errorText = await res.text().catch(() => res.statusText);
            console.error('Transcription API error:', errorText);
            return;
          }

          const json = await res.json();
          const text = json.text || json.transcript || json.partial;
          if (text?.trim()) {
            const wordCount = text.trim().split(/\s+/).length;
            onText(text.trim());
            if (wordCount >= 3) {
              fullTranscriptRef.current += (fullTranscriptRef.current ? ' ' : '') + text.trim();
            }
          }
        } catch (err) {
          console.error('Transcription error:', err);
        }
      }
    };

    return () => {
      processor.disconnect();
      source.disconnect();
      controllerRef.current?.abort();
      audioContext.close();
      setIsStreaming(false);
    };
  }, [audioTrack, onText]);

  //
  // send transcript every hour & on unmount
  //
  useEffect(() => {
    const sendTranscript = async () => {
      const transcript = fullTranscriptRef.current.trim();
      if (!transcript) return;

      const other_people_hash = participants.map(p => p.identity).join(',');
      const user_hash = window.serverData?.user_hash || 'unknown';
      const code = window.serverData?.code || 'fail';
      const now = new Date();
      const start_time = window.serverData?.start_time ? Number(window.serverData.start_time) : now.getTime();
      const minutes = Math.floor((now.getTime() - start_time) / 60000);
      const meta = {
        user_hash: user_hash,
        other_people : other_people_hash || 'none',
        meeting_code: code,
        time_amount : minutes,
        date : new Date().toISOString().split('T')[0],
        reporting_entity_hash :'2f6f32cac1d23ec8db53d1e19e3a8b5e',  // hash remote video-chat.knocscore.com   // 'b61d9602eaf73a7a8d484b47310f9118', local // video-chat
        // Changed to give a common meeting code for all participating users in a session. Although different from the actual unque urls
        // for each user.
        source : 'video-chat.knocscore.com/' + code, // need a unique source
        url_hash: window.serverData?.url_hash || 'unknown',
      }

      const payload = {
        is_end_session : isEndOfSession,
        meta: meta,
        corpus: transcript,
      };

      console.log('The transcript: ' + payload.corpus);

      try {
        await fetch(process.env.REACT_APP_VERTX_LINK + '/CL55/CLSI1012', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        console.log('Transcript sent successfully:', payload);
      } catch (err) {
        console.error('Error sending transcript:', err);
      }
    };

    // Send every 1 hour (3600000 ms)
    const interval = setInterval(sendTranscript, 3600000);
    // const interval = setInterval(sendTranscript, 30000);

    // Also send once when component unmounts
    return () => {
      isEndOfSession = true;
      clearInterval(interval);
      sendTranscript();
    };
  }, [participantName, participants]);

  return { isStreaming, error };
}
